import consoleLogs from '../db/consoleLogs.js';
import formatMessage from '../utils/formatMessage.js';
import { serializeLogValue } from '../utils/serializeLogValue.js';

class ConsoleLogger {
  constructor() {
    this.originalConsole = {
      log: console.log,
      warn: console.warn,
      error: console.error,
      info: console.info
    };

    this.isInitialized = false;
    this.pendingLogs = [];
  }

  // Initialize the console logger
  async init() {
    if (this.isInitialized) return;

    try {
      // Override console methods
      this.overrideConsoleMethods();

      // Process any pending logs
      await this.processPendingLogs();

      this.isInitialized = true;
      this.originalConsole.log('Console logger initialized');
    } catch (error) {
      this.originalConsole.error('Failed to initialize console logger:', error);
    }
  }

  // Override native console methods
  overrideConsoleMethods() {
    const self = this;

    console.log = function (...args) {
      self.log('log', ...args);
      self.originalConsole.log(...args);
    };

    console.warn = function (...args) {
      self.log('warn', ...args);
      self.originalConsole.warn(...args);
    };

    console.error = function (...args) {
      self.log('error', ...args);
      self.originalConsole.error(...args);
    };

    console.info = function (...args) {
      self.log('info', ...args);
      self.originalConsole.info(...args);
    };
  }

  // Restore original console methods
  restore() {
    console.log = this.originalConsole.log;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;
    console.info = this.originalConsole.info;
    this.isInitialized = false;
  }

  // Log a message
  async log(level, ...args) {
    try {
      if (!this.isInitialized) {
        // Store for later processing
        this.pendingLogs.push({ level, args, timestamp: new Date() });
        return;
      }

      // Check if logging is enabled
      const recordLogs = localStorage.getItem('recordLogs') === 'true';
      if (!recordLogs) {
        // Still call original console methods but don't store logs
        this.originalConsole[level]?.(...args);
        return;
      }

      const formattedMessage = formatMessage(args);
      const data = args.slice(1).map(serializeLogValue);
      const location = this.getLocation();

      await consoleLogs.add(level, formattedMessage, data, 'app', location);
    } catch (error) {
      // Fallback to original console to avoid infinite loops
      this.originalConsole.error('Error logging to console logger:', error);
    }
  }



  // Get current location (file and line number)
  getLocation() {
    try {
      const stack = new Error().stack;
      const lines = stack.split('\n');

      // Find the first line that's not from the console logger
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.includes('consoleLogger') || line.includes('ConsoleLogger')) {
          continue;
        }
        if (line.includes('at ') && (line.includes('.js') || line.includes('.jsx'))) {
          // Extract file and line info
          const match = line.match(/at .*?\((.*?):(\d+):(\d+)\)/);
          if (match) {
            const [, file, line, column] = match;
            const fileName = file.split('/').pop();
            return `${fileName}:${line}`;
          }
        }
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  // Process any logs that were captured before initialization
  async processPendingLogs() {
    if (this.pendingLogs.length === 0) return;

    try {
      for (const pendingLog of this.pendingLogs) {
        const formattedMessage = formatMessage(pendingLog.args);
        const data = pendingLog.args.slice(1).map(serializeLogValue);

        await consoleLogs.add(
          pendingLog.level,
          formattedMessage,
          data,
          'app',
          null
        );
      }

      this.originalConsole.log(`Processed ${this.pendingLogs.length} pending logs`);
      this.pendingLogs = [];
    } catch (error) {
      this.originalConsole.error('Error processing pending logs:', error);
    }
  }

  // Log from service worker
  async logFromServiceWorker(level, message, data = [], location = null) {
    try {
      await consoleLogs.add(level, message, data, 'service-worker', location);
    } catch (error) {
      this.originalConsole.error('Error logging from service worker:', error);
    }
  }

  // Log from component
  async logFromComponent(level, message, data = [], componentName = null) {
    try {
      await consoleLogs.add(level, message, data, 'component', componentName);
    } catch (error) {
      this.originalConsole.error('Error logging from component:', error);
    }
  }

  // Get logs with filtering
  async getLogs(options = {}) {
    try {
      return await consoleLogs.get(options);
    } catch (error) {
      this.originalConsole.error('Error getting logs:', error);
      return [];
    }
  }

  // Get log statistics
  async getStats() {
    try {
      return await consoleLogs.getStats();
    } catch (error) {
      this.originalConsole.error('Error getting log stats:', error);
      return null;
    }
  }

  // Clear all logs
  async clearLogs() {
    try {
      await consoleLogs.clear();
      this.originalConsole.log('All console logs cleared');
    } catch (error) {
      this.originalConsole.error('Error clearing logs:', error);
    }
  }

  // Delete old logs
  async deleteOldLogs(days = 7) {
    try {
      const deletedCount = await consoleLogs.deleteOlderThan(days);
      this.originalConsole.log(`Deleted ${deletedCount} logs older than ${days} days`);
      return deletedCount;
    } catch (error) {
      this.originalConsole.error('Error deleting old logs:', error);
      return 0;
    }
  }
}

// Create singleton instance
const consoleLogger = new ConsoleLogger();

export default consoleLogger; 